import { router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../../../components/ui';
import ThemeToggle from '../../../components/ui/ThemeToggle';
import { useTheme } from '../../../lib/ThemeContext';
import lightReadOnPaper from '../../../../assets/images/light_read_on_paper.jpg';
import darkReadOnPaper from '../../../../assets/images/dark_read_on_paper.jpg';
import lightReadOnScreen from '../../../../assets/images/light_read_on_screen.jpg';
import darkReadOnScreen from '../../../../assets/images/dark_read_on_screen.jpg';

export default function PracticeChoose({
  student,
  session,
  stimulus,
  activity,
}) {
  const { t } = useTranslation();
  const { dark } = useTheme();
  const readOnPaperImg = dark ? darkReadOnPaper : lightReadOnPaper;
  const readOnScreenImg = dark ? darkReadOnScreen : lightReadOnScreen;

  const handleReadOnScreen = () => {
    router.visit(`/student/practice/${session.id}/read`);
  };

  const stimulusType = stimulus?.stimulus_type;

  const handleDownloadPdf = () => {
    // For PDFs, open the file directly; for articles, generate a PDF via the server
    window.open(`/student/practice/${session.id}/download_stimulus`, '_blank');
    router.visit(`/student/practice/${session.id}/contemplation`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 dark:bg-gray-800 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-6 py-6 relative">
          <div className="absolute right-6 top-6">
            <ThemeToggle />
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {activity?.title || t('practiceChoose.title')}
            </h1>
            {stimulusType !== 'url' && (
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {t('practiceChoose.subtitle')}
              </p>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Introduction */}
        <div className="text-center mb-12">
          <p className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
            {t('practiceChoose.researchShows')} <em>{t('practiceChoose.how')}</em> {t('practiceChoose.you')} read affects <em>{t('practiceChoose.what')}</em> {t('practiceChoose.youRemember')}
            {' '}{t('practiceChoose.noWrongChoice')}
          </p>
        </div>

        {/* Choice Cards */}
        {/* TODO: For url type, add reader view with print support in the future */}
        <div className={`grid ${stimulusType === 'url' ? 'max-w-lg mx-auto' : 'md:grid-cols-2'} gap-8 mb-12`}>
          {/* Left: Print & Read Offline (hidden for url type) */}
          {stimulusType !== 'url' && (
          <Card className="relative overflow-hidden border-2 border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600 transition-colors">
            {/* Illustration Area */}
            <div className="h-48 overflow-hidden">
              <img src={readOnPaperImg} alt={t('practiceChoose.printTitle')} className="w-full h-full object-cover" />
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t('practiceChoose.printTitle')}
                </h2>
              </div>

              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('practiceChoose.printDescription')}
              </p>

              {/* Research Points */}
              <div className="space-y-4 mb-6">
                <InfoPoint
                  icon="brain"
                  title={t('practiceChoose.deeperProcessing')}
                  description={t('practiceChoose.deeperProcessingDesc')}
                />
                <InfoPoint
                  icon="target"
                  title={t('practiceChoose.betterCalibration')}
                  description={t('practiceChoose.betterCalibrationDesc')}
                />
                <InfoPoint
                  icon="focus"
                  title={t('practiceChoose.fewerDistractions')}
                  description={t('practiceChoose.fewerDistractionsDesc')}
                />
              </div>

              {/* Best For */}
              <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300 mb-2">{t('practiceChoose.bestFor')}</p>
                <ul className="text-sm text-emerald-700 dark:text-emerald-400 space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckIcon />
                    {t('practiceChoose.deepStudy')}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckIcon />
                    {t('practiceChoose.longTexts')}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckIcon />
                    {t('practiceChoose.rememberDetails')}
                  </li>
                </ul>
              </div>

              {/* CTA */}
              <Button
                variant="secondary"
                size="lg"
                className="w-full"
                onClick={handleDownloadPdf}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {t('practiceChoose.downloadPdf')}
              </Button>

              {stimulus?.page_count && (
                <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-3">
                  {stimulus.page_count} {t('common.pages')} · ~{stimulus.estimated_read_time} {t('practiceChoose.minRead')}
                </p>
              )}
            </div>
          </Card>
          )}

          {/* Right: Read On Screen */}
          <Card className="relative overflow-hidden border-2 border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600 transition-colors">
            {/* Illustration Area */}
            <div className="h-48 overflow-hidden">
              <img src={readOnScreenImg} alt={t('practiceChoose.screenTitle')} className="w-full h-full object-cover" />
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t('practiceChoose.screenTitle')}
                </h2>
              </div>

              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('practiceChoose.screenDescription')}
              </p>

              {/* Research Points */}
              <div className="space-y-4 mb-6">
                <InfoPoint
                  icon="speed"
                  title={t('practiceChoose.fasterNavigation')}
                  description={t('practiceChoose.fasterNavigationDesc')}
                />
                <InfoPoint
                  icon="interactive"
                  title={t('practiceChoose.interactiveFeatures')}
                  description={t('practiceChoose.interactiveFeaturesDesc')}
                />
                <InfoPoint
                  icon="agile"
                  title={t('practiceChoose.agileLearning')}
                  description={t('practiceChoose.agileLearningDesc')}
                />
              </div>

              {/* Best For */}
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">{t('practiceChoose.bestFor')}</p>
                <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckIcon color="blue" />
                    {t('practiceChoose.quickScanning')}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckIcon color="blue" />
                    {t('practiceChoose.interactiveLearning')}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckIcon color="blue" />
                    {t('practiceChoose.shorterTexts')}
                  </li>
                </ul>
              </div>

              {/* CTA */}
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={handleReadOnScreen}
              >
                {t('practiceChoose.beginReading')}
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Button>

              {stimulus?.estimated_read_time && (
                <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-3">
                  ~{stimulus.estimated_read_time} {t('practiceChoose.minRead')}
                </p>
              )}
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}

function InfoPoint({ icon, title, description }) {
  const icons = {
    brain: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    target: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    focus: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
    speed: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    interactive: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
      </svg>
    ),
    agile: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  };

  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400">
        {icons[icon]}
      </div>
      <div>
        <p className="font-medium text-gray-900 dark:text-white text-sm">{title}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
      </div>
    </div>
  );
}

function CheckIcon({ color = 'emerald' }) {
  const colorClass = color === 'blue' ? 'text-blue-600' : 'text-emerald-600';
  return (
    <svg className={`w-4 h-4 ${colorClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}
